import { Empty } from "antd";
import {
    type BTFileSystem,
    type FileViewOptions,
} from "legacyComponents/FileUploadContainer.types";
import { useCallback, useContext } from "react";
import { useHistory, useRouteMatch } from "react-router";
import { type IBaseSlide } from "yet-another-react-lightbox";

import { BTButton } from "@btnet/core-components";
import { cn } from "@btnet/utilities-core";

import { UserInfoContext } from "helpers/globalContext/UserInfoContext";
import { useWebviewContext } from "helpers/webview/WebviewContext";

import { type BTLoginTypes, MediaType } from "types/enum";

import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import { BTLayoutContent } from "commonComponents/btWrappers/BTLayout/BTLayout";
import { BTLightbox } from "commonComponents/btWrappers/BTLightbox/BTLightbox";
import {
    btFileSystemToSlide,
    canViewBTFileSystemInLightbox,
} from "commonComponents/btWrappers/BTLightbox/BTLightbox.utility";
import { type IModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";
import { BTModalLayout } from "commonComponents/btWrappers/BTModal/BTModalLayout";
import { BTPopover } from "commonComponents/btWrappers/BTPopover/BTPopover";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { getAttachmentOverlayItems } from "commonComponents/entity/media/common/MediaContextMenu/MediaContextMenu.utility";
import { AttachmentOverlay } from "commonComponents/entity/media/common/MediaContextMenu/Overlays/AttachmentOverlay";
import { MediaTile } from "commonComponents/entity/media/common/MediaGrid/MediaTile";
import { MediaTable } from "commonComponents/entity/media/common/MediaTable/MediaTable";
import { FileViewToggle } from "commonComponents/entity/media/FileViewToggle/FileViewToggle";
import { type ViewAllAttachmentsActions } from "commonComponents/entity/media/ViewAllAttachments/ViewAllAttachments.types";
import { type SharingSettingsAPI } from "commonComponents/entity/sharing/SharingContainer/SharingContainer.types";
import { PageSection } from "commonComponents/utilities/PageSection/PageSection";

import { buildAttachedMediaPropertiesRouting } from "entity/media/common/media.utils";
import { type AnnotationType, type SelectedMediaListViews } from "entity/media/common/mediaTypes";

import "./ViewAllAttachments.less";

interface IViewAllAttachmentsPresentationalProps {
    files: BTFileSystem[];
    lightboxSelectedId: number | null;
    view: SelectedMediaListViews;
    actionBeingPerformed: ViewAllAttachmentsActions;
    modalConfig?: IModalConfiguration;
    sharingSettings?: SharingSettingsAPI;
    isReadonly?: boolean;
    fileViewOptions?: FileViewOptions;
    entityId: number;
    entityName: string;
    onOpenLightbox: (id: number) => void;
    onSlideChange: (_: number, slide: IBaseSlide) => void;
    onCloseLightbox: () => void;
    onViewChange: (value: SelectedMediaListViews) => void;
    onBulkDownload: () => Promise<void>;
    onVideoDownload: (file: BTFileSystem) => Promise<void>;
    onAddClicked?: () => void;
    onViewPermissionsUpdate?: (file: BTFileSystem, loginType: BTLoginTypes) => Promise<void>;
    onNotify: (file: BTFileSystem, loginType: BTLoginTypes) => Promise<void>;
    onFileDraw?: (
        docInstanceId: number,
        annotationType: AnnotationType,
        mediaType: MediaType,
    ) => Promise<void>;
    onEditOnline?: (docInstanceId: number, mediaType: MediaType) => Promise<void>;
    onFileDelete?: (file: BTFileSystem) => Promise<void>;
    isOnExternalPage?: boolean;
}

export const ViewAllAttachmentsPresentational = ({
    files,
    lightboxSelectedId,
    view,
    actionBeingPerformed,
    modalConfig,
    sharingSettings,
    isReadonly,
    fileViewOptions,
    entityId,
    entityName,
    onOpenLightbox,
    onSlideChange,
    onCloseLightbox,
    onViewChange,
    onBulkDownload,
    onVideoDownload,
    onAddClicked,
    onViewPermissionsUpdate,
    onNotify,
    onFileDraw,
    onEditOnline,
    onFileDelete,
    isOnExternalPage = false,
}: IViewAllAttachmentsPresentationalProps) => {
    const userInfo = useContext(UserInfoContext);
    const webview = useWebviewContext();

    const history = useHistory();
    const match = useRouteMatch();

    const handleClickThumbnail = useCallback(
        (e: React.MouseEvent<HTMLImageElement, MouseEvent>, file: BTFileSystem) => {
            // stop row from being selected
            e.stopPropagation();
            const canViewInLightbox = canViewBTFileSystemInLightbox(file);
            if (canViewInLightbox) {
                onOpenLightbox(file.id);
            } else if (file.isTempFile || file.mediaType === MediaType.Video) {
                return;
            } else {
                const link = buildAttachedMediaPropertiesRouting(
                    { fileId: file.id, mediaType: file.mediaType, jobId: file.jobsiteId },
                    entityName,
                    entityId,
                );
                history.push(match.url + link);
            }
        },
        [entityId, entityName, history, match.url, onOpenLightbox],
    );

    const slides = files.filter(canViewBTFileSystemInLightbox).map(btFileSystemToSlide);
    const index = slides.findIndex((s) => s.metadata?.mediaId === lightboxSelectedId);
    const open = index >= 0;
    const downloadAllCount = files.filter((f) => f.mediaType !== MediaType.Video).length;
    const hasVideo = downloadAllCount < files.length;
    return (
        <BTModalLayout title="Attachments" modalConfig={modalConfig}>
            <BTLayoutContent>
                <div className="ViewAllAttachments">
                    <PageSection
                        isStickyHeader
                        title={
                            <div className="flex justify-content-between">
                                <div className="buttons">
                                    {downloadAllCount > 0 && (
                                        <BTPopover
                                            disabled={
                                                actionBeingPerformed !== undefined || !hasVideo
                                            }
                                            content="Videos cannot be downloaded at this time."
                                        >
                                            <BTButton
                                                data-testid="downloadAll"
                                                type="secondary"
                                                onClick={onBulkDownload}
                                                actionBeingPerformed={actionBeingPerformed}
                                                loadingAction="download"
                                            >
                                                Download All ({downloadAllCount})
                                            </BTButton>
                                        </BTPopover>
                                    )}
                                    {!isReadonly && (
                                        <BTButton
                                            data-testid="addNewFile"
                                            onClick={onAddClicked}
                                            type="primary"
                                            className="margin-left-xs"
                                        >
                                            Add
                                        </BTButton>
                                    )}
                                </div>

                                <FileViewToggle value={view} onChange={onViewChange} />
                            </div>
                        }
                    >
                        {view === "Table" && (
                            <MediaTable
                                files={files}
                                onVideoDownload={onVideoDownload}
                                onViewPermissionsUpdate={onViewPermissionsUpdate}
                                onNotify={onNotify}
                                onFileDraw={onFileDraw}
                                onEditOnline={onEditOnline}
                                onFileDelete={onFileDelete}
                                isReadOnly={isReadonly}
                                sharingSettings={sharingSettings}
                                onOpenLightbox={onOpenLightbox}
                                fileViewOptions={fileViewOptions}
                                isShowingMore
                                entityId={entityId}
                                entityName={entityName}
                            />
                        )}
                        {view === "Tile" && (
                            <div className={cn("FilePickerGrid", "padding-bottom-md")}>
                                {files.length === 0 && (
                                    <Empty
                                        description="No attachments"
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    />
                                )}
                                {files.length > 0 && (
                                    <BTRow gutter={[8, 8]}>
                                        {files.map((file) => {
                                            const items = getAttachmentOverlayItems(
                                                file,
                                                !!isReadonly,
                                                userInfo?.loginType,
                                                sharingSettings,
                                                false,
                                                false,
                                                !!webview,
                                                fileViewOptions,
                                                isOnExternalPage,
                                                undefined,
                                                !!webview,
                                            );
                                            return (
                                                <BTCol key={file.id} xs={8} sm={6} md={4}>
                                                    <MediaTile
                                                        file={file}
                                                        onClickThumbnail={(e) =>
                                                            handleClickThumbnail(e, file)
                                                        }
                                                        contextMenuProps={{
                                                            renderOverlay: (onMenuClick) => (
                                                                <AttachmentOverlay
                                                                    items={items}
                                                                    file={file}
                                                                    isReadOnly={isReadonly}
                                                                    onClickDownloadVideo={
                                                                        onVideoDownload
                                                                    }
                                                                    onFilePermissionsUpdate={
                                                                        onViewPermissionsUpdate
                                                                    }
                                                                    onClickNotify={onNotify}
                                                                    onClickDraw={onFileDraw}
                                                                    onClickEditOnline={onEditOnline}
                                                                    onClickDelete={onFileDelete}
                                                                    onMenuClick={onMenuClick}
                                                                />
                                                            ),
                                                            items,
                                                        }}
                                                    />
                                                </BTCol>
                                            );
                                        })}
                                    </BTRow>
                                )}
                            </div>
                        )}
                    </PageSection>
                </div>
            </BTLayoutContent>
            <BTLightbox
                slides={slides}
                index={index}
                open={open}
                onClose={onCloseLightbox}
                onSlideChange={onSlideChange}
            />
        </BTModalLayout>
    );
};
